import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Search, X, Truck, Calendar, User, Package, Printer } from 'lucide-react';
import ConsignmentEntryFormLT from './ConsignmentEntryFormLT';
import { printConsignment } from '../utils/printUtils';
import apiClient from '../services/api';

const PRIMARY      = '#2563EB';
const PRIMARY_DARK = '#1D4ED8';

const ConsignmentLT = () => {
  const [consignments, setConsignments] = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [error,        setError]        = useState('');
  const [showForm,     setShowForm]     = useState(false);
  const [editData,     setEditData]     = useState(null);
  const [searchTerm,   setSearchTerm]   = useState('');

  const getSessionData = () => {
    try {
      const s = localStorage.getItem('ltms_session');
      if (!s) return null;
      const d = JSON.parse(s);
      return d?.session || d;
    } catch { return null; }
  };
  const session = getSessionData();

  useEffect(() => { loadConsignments(); }, []);

  const loadConsignments = async () => {
    setLoading(true); setError('');
    try {
      const res = await apiClient.get('/ConsignmentsLT.php');
      if (res.data.success) setConsignments(res.data.data.consignments || []);
      else throw new Error(res.data.message || 'Failed to load');
    } catch (err) {
      let msg = 'Failed to load consignments';
      if (err.response?.status === 401) { msg = 'Session expired. Please login again.'; setTimeout(() => { window.location.href = '/login'; }, 2000); }
      else if (err.response) msg = err.response.data?.message || msg;
      else if (err.request)  msg = 'Server not responding. Please check if backend is running.';
      else                   msg = err.message;
      setError(msg);
    } finally { setLoading(false); }
  };

  const handleNew   = () => { setEditData(null); setShowForm(true); };
  const handleClose = () => { setShowForm(false); setEditData(null); };

  const handleEdit = async (consignment) => {
    try {
      const res = await apiClient.get(`/ConsignmentsLT.php/${consignment.ConsignmentID}`);
      if (res.data.success) { setEditData(res.data.data); setShowForm(true); }
      else alert('Error loading consignment: ' + res.data.message);
    } catch (err) { alert('Error loading consignment: ' + (err.response?.data?.message || err.message)); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this consignment?')) return;
    try {
      const res = await apiClient.delete(`/ConsignmentsLT.php/${id}`);
      if (res.data.success) { alert('Consignment deleted successfully!'); loadConsignments(); }
      else alert('Error: ' + res.data.message);
    } catch (err) { alert('Error deleting consignment: ' + (err.response?.data?.message || err.message)); }
  };

  const handleSave = async (formData) => {
    try {
      let res;
      if (formData.ConsignmentID) {
        res = await apiClient.put('/ConsignmentsLT.php', formData);
        if (res.data.success) alert(`Consignment updated successfully: ${res.data.data.ConsignmentNo}`);
      } else {
        res = await apiClient.post('/ConsignmentsLT.php', formData);
        if (res.data.success) alert(`Consignment created: ${res.data.data.consignmentNo}`);
      }
      if (!res.data.success) throw new Error(res.data.message);
      setShowForm(false); setEditData(null); loadConsignments();
    } catch (err) {
      const msg = err.response?.data?.message || err.message;
      alert('Error saving consignment: ' + msg);
      throw new Error(msg);
    }
  };

  const handlePrint = (consignment) => { printConsignment(consignment, 'LT'); };

  if (!session?.companyid) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center p-10 bg-white rounded-xl shadow-sm border border-[#E2E8F0]">
          <Truck size={40} className="mx-auto mb-4 text-gray-300" />
          <h3 className="text-lg font-bold text-gray-700 mb-2">No Session Context</h3>
          <p className="text-gray-500 text-sm">Please select a company and financial year from the dashboard.</p>
        </div>
      </div>
    );
  }

  const filteredConsignments = consignments.filter(c =>
    c.ConsignmentNo?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.ShipperName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.ConsigneeName?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Maps to the Status Badges spec: Paid/Processing/Pending/Cancelled tokens
  const statusCls = (color) => {
    const map = {
      green:   'bg-[#DCFCE7] text-[#16A34A]',
      success: 'bg-[#DCFCE7] text-[#16A34A]',
      yellow:  'bg-[#FEF3C7] text-[#F59E0B]',
      warning: 'bg-[#FEF3C7] text-[#F59E0B]',
      red:     'bg-[#FEE2E2] text-[#DC2626]',
      danger:  'bg-[#FEE2E2] text-[#DC2626]',
      blue:    'bg-[#DBEAFE] text-[#2563EB]',
      info:    'bg-[#DBEAFE] text-[#2563EB]',
    };
    return map[color] || 'bg-gray-100 text-gray-600';
  };

  return (
    <div className="flex flex-col bg-[#F8FAFC] min-h-screen">

      {/* HEADER */}
      <div className="flex items-center justify-between px-6 py-4 flex-shrink-0"
        style={{ background: `linear-gradient(135deg, ${PRIMARY} 0%, ${PRIMARY_DARK} 100%)` }}>
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-white/25 flex items-center justify-center flex-shrink-0">
            <Truck size={26} color="#fff" />
          </div>
          <div>
            <h1 className="text-xl font-bold leading-tight" style={{ color: '#fff' }}>Land Transport Consignments</h1>
            <p className="text-[13px]" style={{ color: 'rgba(255,255,255,0.85)' }}>Manage LT consignments and shipments</p>
          </div>
        </div>
        <button onClick={handleNew}
          className="flex items-center gap-2 px-4 py-2.5 bg-white rounded-lg border-0 text-sm font-bold cursor-pointer transition-all duration-150 hover:bg-[#EFF6FF]"
          style={{ color: PRIMARY_DARK }}>
          <Plus size={17} /> New Consignment
        </button>
      </div>

      {/* TOOLBAR */}
      <div className="flex items-center gap-3 flex-wrap px-5 py-3 bg-white border-b border-[#E2E8F0] flex-shrink-0">
        <div className="flex-1 min-w-[280px] flex items-center gap-2 bg-gray-50 border-2 border-[#CBD5E1] rounded-lg px-3
          focus-within:border-[#2563EB] focus-within:bg-white focus-within:shadow-[0_0_0_4px_rgba(37,99,235,0.12)] transition-all duration-200">
          <Search size={16} className="text-gray-400 flex-shrink-0" />
          <input type="text" placeholder="Search by consignment no, shipper, or consignee..."
            value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
            className="flex-1 border-0 outline-none py-2.5 text-sm text-gray-800 bg-transparent font-medium placeholder:text-gray-400 placeholder:font-normal" />
          {searchTerm && (
            <button onClick={() => setSearchTerm('')}
              className="p-1 rounded cursor-pointer bg-transparent border-0 text-gray-400 hover:text-[#DC2626] hover:bg-gray-100">
              <X size={14} />
            </button>
          )}
        </div>
        <div className="flex items-center gap-2 text-sm font-medium text-gray-500 whitespace-nowrap">
          <span className="inline-block px-2.5 py-1 rounded-full text-[12px] font-bold text-white"
            style={{ background: PRIMARY }}>{filteredConsignments.length}</span>
          {filteredConsignments.length === 1 ? 'consignment' : 'consignments'}
        </div>
      </div>

      {/* BODY */}
      <div className="flex-1 p-5 overflow-auto">
        {loading ? (
          <div className="flex flex-col items-center justify-center py-20 gap-4 text-gray-400">
            <div className="w-10 h-10 border-4 border-gray-200 border-t-[#2563EB] rounded-full animate-spin" />
            <p className="text-sm">Loading consignments...</p>
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center py-20 gap-5">
            <div className="px-5 py-4 bg-[#FEE2E2] border border-red-200 rounded-lg text-[#DC2626] text-sm font-medium">
              <strong>Error:</strong> {error}
            </div>
            <button onClick={loadConsignments}
              className="px-4 py-2.5 text-sm font-bold text-white border-0 rounded-lg cursor-pointer"
              style={{ background: `linear-gradient(135deg, ${PRIMARY} 0%, ${PRIMARY_DARK} 100%)` }}>
              Retry
            </button>
          </div>
        ) : filteredConsignments.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 gap-4 text-center bg-white rounded-xl border border-[#E2E8F0]">
            <Truck size={48} className="text-gray-300" />
            <h3 className="text-lg font-bold text-gray-600">No consignments found</h3>
            <p className="text-gray-400 text-sm max-w-[380px]">
              {searchTerm ? 'Try adjusting your search terms' : 'Get started by creating your first consignment'}
            </p>
            {!searchTerm && (
              <button onClick={handleNew}
                className="flex items-center gap-2 px-4 py-2.5 text-sm font-bold text-white border-0 rounded-lg cursor-pointer mt-2"
                style={{ background: `linear-gradient(135deg, ${PRIMARY} 0%, ${PRIMARY_DARK} 100%)` }}>
                <Plus size={16} /> New Consignment
              </button>
            )}
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-[#E2E8F0] shadow-sm overflow-hidden">
            <div className="overflow-x-auto" style={{ maxHeight: 'calc(100vh - 280px)', overflowY: 'auto' }}>
              <table className="w-full border-collapse" style={{ minWidth: 960, tableLayout: 'fixed' }}>
                <thead className="sticky top-0 z-10">
                  <tr style={{ background: `linear-gradient(135deg, ${PRIMARY} 0%, ${PRIMARY_DARK} 100%)` }}>
                    {[['Consignment No','13%'],['Date','8%'],['Shipper','13%'],['Consignee','13%'],['Origin → Destination','18%'],['Weight (kg)','9%'],['Status','9%'],['Actions','12%']].map(([h,w]) => (
                      <th key={h} style={{ width: w }}
                        className="px-4 py-3.5 text-left text-[11px] font-bold uppercase tracking-wider text-white border-r border-white/20 last:border-r-0">
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filteredConsignments.map(c => (
                    <tr key={c.ConsignmentID}
                      className="border-b border-gray-100 transition-all duration-150 hover:bg-[#EFF6FF] hover:translate-x-0.5 hover:shadow-[-2px_0_0_#2563EB]">
                      <td className="px-4 py-3.5">
                        <span className="font-mono text-[12px] font-bold text-[#1D4ED8] bg-[#EFF6FF] px-2 py-0.5 rounded">{c.ConsignmentNo}</span>
                      </td>
                      <td className="px-4 py-3.5">
                        <div className="flex items-center gap-1.5 text-gray-500 text-sm">
                          <Calendar size={13} className="text-gray-400 flex-shrink-0" />
                          {new Date(c.ConsignmentDate).toLocaleDateString()}
                        </div>
                      </td>
                      <td className="px-4 py-3.5">
                        <div className="flex items-start gap-1.5">
                          <User size={13} className="text-gray-400 mt-0.5 flex-shrink-0" />
                          <div>
                            <div className="font-medium text-gray-800 text-sm truncate">{c.ShipperName || '-'}</div>
                            {c.ShipperCode && <div className="font-mono text-[11px] text-gray-400">{c.ShipperCode}</div>}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3.5">
                        <div className="flex items-start gap-1.5">
                          <User size={13} className="text-gray-400 mt-0.5 flex-shrink-0" />
                          <div>
                            <div className="font-medium text-gray-800 text-sm truncate">{c.ConsigneeName || '-'}</div>
                            {c.ConsigneeCode && <div className="font-mono text-[11px] text-gray-400">{c.ConsigneeCode}</div>}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3.5 text-[13px] text-gray-500 whitespace-nowrap">
                        {c.OriginCity || 'N/A'} → {c.DestinationCity || 'N/A'}
                      </td>
                      <td className="px-4 py-3.5">
                        <div className="flex items-center gap-1.5 font-mono font-semibold text-[#1D4ED8] text-sm">
                          <Package size={13} className="text-gray-400 flex-shrink-0" />
                          {c.Weight ? parseFloat(c.Weight).toFixed(2) : '0.00'}
                        </div>
                      </td>
                      <td className="px-4 py-3.5">
                        <span className={`inline-block px-3 py-1 rounded-full text-[11px] font-bold uppercase tracking-wide ${statusCls(c.StatusColor)}`}>
                          {c.Status || 'Pending'}
                        </span>
                      </td>
                      <td className="px-4 py-3.5">
                        <div className="flex gap-1.5">
                          <button onClick={() => handlePrint(c)} title="Print"
                            className="w-8 h-8 flex items-center justify-center rounded-lg border cursor-pointer border-[#93C5FD] text-[#0891B2] bg-[#ECFEFF] transition-all duration-150 hover:bg-[#0891B2] hover:text-white hover:scale-110 hover:-translate-y-0.5">
                            <Printer size={13} />
                          </button>
                          <button onClick={() => handleEdit(c)} title="Edit"
                            className="w-8 h-8 flex items-center justify-center rounded-lg border cursor-pointer border-[#93C5FD] text-[#2563EB] bg-[#EFF6FF] transition-all duration-150 hover:bg-[#2563EB] hover:text-white hover:scale-110 hover:-translate-y-0.5">
                            <Edit2 size={13} />
                          </button>
                          <button onClick={() => handleDelete(c.ConsignmentID)} title="Delete"
                            className="w-8 h-8 flex items-center justify-center rounded-lg border cursor-pointer border-red-200 text-[#DC2626] bg-[#FEE2E2] transition-all duration-150 hover:bg-[#DC2626] hover:text-white hover:scale-110 hover:-translate-y-0.5">
                            <Trash2 size={13} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {showForm && <ConsignmentEntryFormLT onClose={handleClose} onSave={handleSave} editData={editData} />}
    </div>
  );
};

export default ConsignmentLT;